import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useBrandDetail } from '@/hooks/useAppData';
import { useEntityPage } from '@/hooks/useEntityPage';
import { useBrandPageHub } from '@/hooks/useBrandPageHub';
import { useTrackView } from '@/hooks/useTrackView';
import { useRenderNow } from '@/hooks/useRenderNow';
import { PageHead } from '@/components/seo/PageHead';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { AboutSection } from '@/components/detail/AboutSection';
import { BrandFaqSection } from '@/components/detail/BrandFaqSection';
import { EntityDetailLayout } from '@/components/detail/EntityDetailLayout';
import { FollowForAlerts } from '@/components/detail/FollowForAlerts';
import { NextSalePattern } from '@/components/detail/NextSalePattern';
import { SaleHistoryTable } from '@/components/detail/SaleHistoryTable';
import { SimilarBrands } from '@/components/detail/SimilarBrands';
import { CategoryChips } from '@/components/category/CategoryChips';
import { PromotionCard } from '@/components/cards';
import { NoActiveOffers } from '@/components/detail/NoActiveOffers';
import { StoreList } from '@/components/detail/StoreList';
import { Chip, SectionHeader } from '@/components/ui';
import { useEntityFollow } from '@/components/detail/entityFollow';
import { offersByPhase } from '@/seo/lifecycle';
import { DEFAULT_LOCALE } from '@/seo/locales';
import { brandPageHead } from '@/seo/pageHeads';
import { cleanName } from '@/seo/text';
import { splitLocalePath } from '@/seo/urls';
import { detailSeoOptions, isObjectId } from '@/utils/detailStatus';

/**
 * Brand detail — /brand/:ref and /pk/en/brand/:ref, an id or a slug (web plan
 * W0.16, `useEntityPage`). Views, favorites and follows use the brand's id.
 *
 * EntityDetailLayout owns the cover, logo, title row, favorite/follow
 * buttons, stats, contact and socials. This page supplies the main-column
 * sections (web plan W0.10 + W3.6): categories; "Active offers" — or "No
 * active offers right now — follow {Brand} for alerts" with the follow
 * button — then "Upcoming offers" when there are any; "About {Brand}" when
 * the brand has a description (backend B3.4); sale history table; "when is
 * the next sale?" (≥ 3 past sales); FAQ; similar brands; follow CTA; every
 * store as a link, grouped by city; tags. A staff `seoOverrides`
 * title/description (B3.4) reaches the head through `brandPageHead`.
 *
 * `brandPage` hub data (W3.6) is fetched in parallel with the entity query.
 * The entity query supplies promotions/stores/description; `brandPage` adds
 * saleHistory and similarBrands. For an ObjectId `:ref` the hub query skips
 * (the page will redirect to the slug URL before the user sees it anyway).
 */
export function BrandDetailPage() {
  const { ref = '' } = useParams<{ ref: string }>();
  const location = useLocation();
  const locale = splitLocalePath(location.pathname)?.locale ?? DEFAULT_LOCALE;
  const renderNow = useRenderNow();

  const { item: brand, status, errorCode, refetch, redirect } = useEntityPage(useBrandDetail);
  useTrackView('brand', brand?.id, status);

  // Title, description, H1 and breadcrumbs from the brand formula (web plan
  // W0.6): "{Brand} Sale Today (…)" only while one of its promotions is live.
  // JSON-LD (W0.7): a CollectionPage about the brand's Organization. The SEO
  // server builds the same head from the same query (web plan W1.3).
  const t = useTranslate();
  const head = brand && status === 'ok' ? brandPageHead(brand, { t, locale }) : null;
  const pageHead = (
    <PageHead
      head={head}
      // Loading / not found / failed load (web plan W0.2): "Brand · PromoStork"
      // or "Brand not found" + noindex, never the homepage title. Kept last.
      {...(status === 'ok' ? {} : detailSeoOptions('brand', status))}
    />
  );

  // Hub query (W3.6): saleHistory + similarBrands, in parallel with the
  // entity query (asked while it loads, from the URL's slug). Skipped for
  // not-found and error, while the page moves to its canonical URL, and for
  // ObjectId refs that redirect to the slug URL before the user sees anything.
  const hubSlug = (status === 'ok' || status === 'loading') && !redirect && !isObjectId(ref) ? ref : null;
  const { hub } = useBrandPageHub(hubSlug, locale);

  const categories = brand?.categories ?? [];
  // By phase from the dates, like the title's "Today": the upcoming ones get
  // their own section, so "No active offers right now" is true when shown.
  // The server's render time while hydrating, so the sections match its HTML.
  const offers = offersByPhase(brand?.promotions, renderNow, locale);
  const stores = brand?.brandStores ?? [];
  const cityCounts = brand?.cityCounts ?? [];
  const tags = brand?.tags ?? [];
  const brandName = brand ? cleanName(brand.name) || brand.name : '';

  // W3.6 hub sections
  const saleHistory = hub?.saleHistory ?? [];
  const similarBrands = hub?.similarBrands ?? [];
  const storeCount = hub?.storeCount ?? stores.length;

  if (redirect) return <Navigate replace to={redirect.to} state={redirect.state} />;

  return (
    <EntityDetailLayout
      kind="brand"
      entity={brand}
      pageHead={pageHead}
      status={status}
      errorCode={errorCode}
      onRetry={refetch}
      h1={head?.h1}
      // The visible trail is the BreadcrumbList's own array (web plan W0.8).
      breadcrumbs={head?.breadcrumbs}
    >
      {brand && (
        <div className="space-y-8">
          {categories.length > 0 && (
            <section aria-label="Categories">
              <CategoryChips categories={categories} segment="brands" />
            </section>
          )}

          <section aria-label="Active offers">
            <SectionHeader title="Active offers" />
            {offers.active.length > 0 ? (
              <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
                {offers.active.map((promotion) => (
                  <PromotionCard key={promotion.id} promotion={promotion} />
                ))}
              </div>
            ) : (
              <NoActiveOffers brandName={brandName} className="mt-4" />
            )}
          </section>

          {offers.upcoming.length > 0 && (
            <section aria-label="Upcoming offers">
              <SectionHeader title="Upcoming offers" />
              <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
                {offers.upcoming.map((promotion) => (
                  <PromotionCard key={promotion.id} promotion={promotion} />
                ))}
              </div>
            </section>
          )}

          {/* "About {Brand}" from the staff-written description (backend
              B3.4, web plan W3.6): plain text, only when there is some. */}
          <AboutSection name={brandName} description={brand.description} />

          {/* Sale history table (W3.6): ended promotions, most recent first.
              Rows link to the promotion page only while urlPath is non-null
              (≤ 90 days after end); after that they are plain text. */}
          {saleHistory.length > 0 && (
            <SaleHistoryTable brandName={brandName} history={saleHistory} />
          )}

          {/* "When is the next sale?" section (W3.6): historical pattern only,
              shown only when ≥ 3 past sales are available. */}
          <NextSalePattern brandName={brandName} saleHistory={saleHistory} />

          {/* FAQ (W3.6): plain HTML <dl>, no FAQPage JSON-LD markup. */}
          <BrandFaqSection
            brandName={brandName}
            storeCount={storeCount}
            saleHistory={saleHistory}
          />

          {/* Similar brands (W3.6): ≤ 12 indexable brands sharing a category. */}
          {similarBrands.length > 0 && (
            <SimilarBrands brands={similarBrands} />
          )}

          {/* Follow CTA repeat at the bottom (W3.6). The layout's header
              already has the Follow button; this surfaces it again once the
              user has scrolled through all the hub content. Only shown when
              the hub query has returned (hub data is loaded). */}
          {hub && <FollowForAlertsCta brandName={brandName} />}

          {stores.length > 0 && (
            <section aria-label="Stores">
              <SectionHeader title={`Stores (${stores.length})`} />
              <StoreList stores={stores} cityCounts={cityCounts} />
            </section>
          )}

          {tags.length > 0 && (
            <section aria-label="Tags">
              <SectionHeader title="Tags" />
              <div className="mt-4 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Chip key={tag.id} label={tag.title} color="neutral" outline />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </EntityDetailLayout>
  );
}

/**
 * "Follow {Brand} for sale alerts" CTA at the bottom of the brand hub
 * (web plan W3.6). Reads the follow state from `EntityFollowContext` (provided
 * by `EntityDetailLayout`); renders nothing when the context is not available
 * (e.g. during loading).
 */
function FollowForAlertsCta({ brandName }: { brandName: string }) {
  const follow = useEntityFollow();
  if (!follow || !brandName) return null;
  return <FollowForAlerts name={brandName} follow={follow} />;
}

export default BrandDetailPage;
